import React from 'react';
import { displayName, nameInitials } from '../../services/displayName';
import { publicOrigin } from '../../services/serverBase.mjs';
import { colors, radii, FONT } from '../../theme';
import { Trash2 } from 'lucide-react';

// ─── Shared Sheet Styles ───────────────────────────────────────
export const sheetStyles = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, animation: 'fadeIn 0.2s' },
  container: { position: 'fixed', bottom: 0, left: 0, right: 0, background: colors.white, borderRadius: `${radii.xl}px ${radii.xl}px 0 0`, padding: '12px 24px', paddingBottom: 'calc(24px + env(safe-area-inset-bottom))', zIndex: 101, animation: 'slideUp 0.3s ease-out', display: 'flex', flexDirection: 'column', gap: 16 },
  handle: { width: 36, height: 5, borderRadius: 3, background: colors.gray200, margin: '0 auto 8px' },
  title: { fontSize: 20, fontWeight: 600, color: colors.gray900 },
  input: { background: colors.gray100, border: `1px solid ${colors.gray200}`, borderRadius: radii.md, padding: '14px 16px', fontSize: 16, fontFamily: FONT, color: colors.gray900, width: '100%' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  label: { fontSize: 15, color: colors.gray600 },
  numInput: { width: 60, textAlign: 'center', background: colors.gray100, border: `1px solid ${colors.gray200}`, borderRadius: radii.md, padding: '10px 8px', fontSize: 16, fontFamily: FONT },
  createBtn: { background: colors.gray900, border: 'none', color: colors.white, padding: '16px', borderRadius: radii.lg, fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, width: '100%' },
  ttlChip: { background: colors.gray100, border: `1px solid ${colors.gray200}`, borderRadius: radii.sm, padding: '8px 12px', fontSize: 13, cursor: 'pointer', fontFamily: FONT, color: colors.gray600 },
  ttlChipActive: { background: colors.gray100, borderColor: colors.gray900, color: colors.gray900, fontWeight: 600 },
};

// ─── CreateLinkSheet ──────────────────────────────────────────
export function CreateLinkSheet({ newLabel, setNewLabel, maxParticipants, setMaxParticipants, ttlHours, setTtlHours, loading, onCreate, onClose }) {
  const s = sheetStyles;
  const ttlOptions = [{ h: 1, label: '1ч' }, { h: 6, label: '6ч' }, { h: 24, label: '24ч' }, { h: 48, label: '2д' }];
  return (
    <>
      <div style={s.backdrop} onClick={onClose} />
      <div style={s.container}>
        <div style={s.handle} />
        <div style={s.title}>Новая ссылка</div>
        <input style={s.input} value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Название (необязательно)" autoFocus onKeyDown={e => e.key === 'Enter' && onCreate()} />
        <div style={s.row}>
          <span style={s.label}>Участников</span>
          <input style={s.numInput} type="number" min="1" max="20" value={maxParticipants} onChange={e => setMaxParticipants(Math.max(1, Math.min(20, Number(e.target.value) || 2)))} />
        </div>
        <div style={s.row}>
          <span style={s.label}>Срок действия</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {ttlOptions.map(o => (
              <button key={o.h} onClick={() => setTtlHours(o.h)} style={{ ...s.ttlChip, ...(ttlHours === o.h ? s.ttlChipActive : {}) }}>{o.label}</button>
            ))}
          </div>
        </div>
        <button onClick={onCreate} disabled={loading} style={s.createBtn}>{loading ? 'Создание...' : 'Создать ссылку'}</button>
      </div>
    </>
  );
}

// ─── QRSheet ──────────────────────────────────────────────────
export function QRSheet({ url, onClose, onCopy }) {
  const QRCodeSVG = require('qrcode.react').QRCodeSVG;
  const s = sheetStyles;
  return (
    <>
      <div style={s.backdrop} onClick={onClose} />
      <div style={{ ...s.container, alignItems: 'center' }}>
        <div style={s.handle} />
        <div style={{ padding: 20, background: colors.white, borderRadius: radii.lg }}>
          <QRCodeSVG value={url} size={200} level="M" />
        </div>
        <div style={{ fontSize: 12, color: colors.gray400, textAlign: 'center', wordBreak: 'break-all', maxWidth: 280 }}>{url}</div>
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          <button onClick={() => { onCopy(); }} style={{ ...s.createBtn, flex: 1 }}>Копировать ссылку</button>
          <button onClick={() => { if (navigator.share) navigator.share({ url, title: 'Voxiox' }); }} style={{ ...s.createBtn, flex: 1, background: colors.gray100, color: colors.gray900 }}>Поделиться</button>
        </div>
      </div>
    </>
  );
}

// ─── ShareContactSheet ────────────────────────────────────────
export function ShareContactSheet({ user, onClose, onCopy }) {
  const QRCodeSVG = require('qrcode.react').QRCodeSVG;
  const s = sheetStyles;
  // ADR-013: share username only. Adding still requires a consent-based friend
  // request; extension is never embedded (it used to leak via ?ext=).
  const url = `${publicOrigin()}/add-contact?name=${encodeURIComponent(user.username)}`;
  return (
    <>
      <div style={s.backdrop} onClick={onClose} />
      <div style={{ ...s.container, alignItems: 'center' }}>
        <div style={s.handle} />
        <div style={s.title}>Мой контакт</div>
        <div style={{ padding: 20, background: colors.white, borderRadius: radii.lg }}>
          <QRCodeSVG value={url} size={200} level="M" />
        </div>
        <div style={{ fontSize: 14, fontWeight: 500, color: colors.gray900, textAlign: 'center' }}>{displayName(user)}</div>
        <div style={{ fontSize: 11, color: colors.gray400, textAlign: 'center', wordBreak: 'break-all', maxWidth: 280 }}>{url}</div>
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          <button onClick={() => onCopy(url)} style={{ ...s.createBtn, flex: 1 }}>Копировать ссылку</button>
          <button onClick={() => { if (navigator.share) navigator.share({ url, title: `Voxiox — ${displayName(user)}` }); }} style={{ ...s.createBtn, flex: 1, background: colors.gray100, color: colors.gray900 }}>Поделиться</button>
        </div>
      </div>
    </>
  );
}

// ─── EditContactSheet ─────────────────────────────────────────
export function EditContactSheet({ contact, setContact, onSave, onDelete, onClose }) {
  const s = sheetStyles;
  return (
    <>
      <div style={s.backdrop} onClick={onClose} />
      <div style={s.container}>
        <div style={s.handle} />
        <div style={s.title}>Редактировать контакт</div>
        <input style={s.input} value={contact.name || ''} onChange={e => setContact(p => ({ ...p, name: e.target.value }))} placeholder="Имя" autoFocus />
        <input style={s.input} value={contact.extension || ''} onChange={e => setContact(p => ({ ...p, extension: e.target.value }))} placeholder="Номер / расширение" />
        <input style={s.input} value={contact.phone || ''} onChange={e => setContact(p => ({ ...p, phone: e.target.value }))} placeholder="Телефон" />
        <input style={s.input} value={contact.notes || ''} onChange={e => setContact(p => ({ ...p, notes: e.target.value }))} placeholder="Заметка" />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onSave} style={{ ...s.createBtn, flex: 1 }}>Сохранить</button>
          <button onClick={onDelete} style={{ ...s.createBtn, flex: 0, background: colors.red, padding: '16px 20px' }}>
            <Trash2 size={18} color="#15141a" />
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * A confirmation that names what will happen, for actions that cannot be undone.
 *
 * `window.confirm` asks "Удалить пользователя?" over a modal the person cannot
 * read past, next to a button they hit by accident — and it does not say that
 * the messages, the files and the call history go with them. The rule here:
 * the consequences are listed, the dangerous button is not the default, and it
 * carries the name of the thing being destroyed.
 */
export function ConfirmSheet({ title, consequences = [], confirmLabel, onConfirm, onClose, busy = false }) {
  return (
    <div style={sheetStyles.backdrop} onClick={onClose}>
      <div style={sheetStyles.container} onClick={(e) => e.stopPropagation()}>
        <div style={sheetStyles.handle} />
        <div style={{ fontFamily: FONT, fontSize: 17, fontWeight: 600, color: colors.gray900 }}>
          {title}
        </div>
        {consequences.length > 0 && (
          <ul style={{
            margin: '0 0 16px', paddingLeft: 18, fontFamily: FONT, fontSize: 14,
            color: colors.gray600, lineHeight: 1.5,
          }}>
            {consequences.map((c, i) => <li key={i} style={{ marginBottom: 4 }}>{c}</li>)}
          </ul>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={onClose} disabled={busy}
            style={{
              background: colors.gray900, border: 'none', color: colors.white,
              padding: '14px 20px', fontSize: 16, fontWeight: 500, borderRadius: radii.sm,
              fontFamily: FONT, cursor: 'pointer',
            }}>
            Отмена
          </button>
          <button onClick={onConfirm} disabled={busy}
            style={{
              background: 'none', border: `1px solid ${colors.red}`, color: colors.red,
              padding: '14px 20px', fontSize: 16, fontWeight: 500, borderRadius: radii.sm,
              fontFamily: FONT, cursor: 'pointer', opacity: busy ? 0.6 : 1,
            }}>
            {busy ? 'Удаляю…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
